import { useCallback, useMemo, useState } from 'react';
import type { DrawingDocument } from '@peruselab/structure-editor/model';
import type { DocumentHistory } from '@peruselab/structure-editor/domain/history';
import {
  EMPTY_MARKUSH_LAYERS,
  addRingAttachment,
  applyAtomList,
  applyRGroupLabel,
  markushIssueAtomIds,
  removeOrphanedAttachmentAtoms,
  type MarkushEdit,
  markushAtomLabels,
  reconcileMarkushLayers,
  toMarkushDrawingDocument,
  withoutPlaceholderAtoms,
  type MarkushLayers
} from '@peruselab/structure-editor/domain/markushLayers';
import {
  nextRGroupLabel,
  referencedRGroupLabels,
  type MarkushDrawingDocument
} from '@peruselab/structure-editor/domain/markushDocument';
import { markushStructureCount } from '@peruselab/structure-editor/domain/markushSummary';
import { markushRepeatUnitMarkers } from '@peruselab/structure-editor/domain/markushProjections';
import { removeRepeatUnitContaining, setRepeatUnit } from '@peruselab/structure-editor/domain/markushMutations';
import type { RepeatUnitRef } from '@peruselab/structure-editor/domain/markushRepeatBrackets';

/** #656 -- a new repeat unit's starting range (edited right after in its pop-up). */
const NEW_REPEAT_MIN = 1;
const NEW_REPEAT_MAX = 3;

type LayerCache = WeakMap<DrawingDocument, MarkushLayers>;

/** What the last rendered present was, and its layers. */
interface Shown {
  readonly document: DrawingDocument;
  readonly layers: MarkushLayers;
}

/** Layers for the history's present entry. Each entry's layers are computed once, while it is
 *  the present, and cached by object identity -- so undo/redo (which only move the same objects
 *  between past, present and future) land on exactly the layers that entry had, even after older
 *  entries have fallen off the capped history. An uncached present was produced from the *last
 *  shown* one -- by a tool commit, or by a replace without history (a perception write-back after
 *  a topology change, a gesture's cache-refresh copy) that never enters past/future -- so it
 *  carries that one's layers across, pruning positions whose atoms were deleted or retyped. An
 *  R-label click caches its own entry before committing it. */
function layersForPresent(document: DrawingDocument, cache: LayerCache, shown: Shown | null): MarkushLayers {
  const cached = cache.get(document);
  if (cached) return cached;
  const layers = shown ? reconcileMarkushLayers(shown.layers, shown.document, document) : EMPTY_MARKUSH_LAYERS;
  // Memoization only: the same entry always derives the same layers.
  cache.set(document, layers);
  return layers;
}

/**
 * #648 -- the Markush layers, layered over useDocumentHistory without changing it.
 *
 * The drawing stays an ordinary DrawingDocument (an R atom is a real atom there, so every tool
 * keeps working), and the Markush-only layers belong to each history entry -- see
 * layersForPresent. #674: there is no Markush mode -- the layers are always live, and a drawing
 * *is* a Markush query exactly while it has a variable position (`hasMarkushContent`). Undo past
 * the first Markush edit is therefore the way back to a plain drawing.
 */
export function useMarkushMode(
  history: DocumentHistory,
  commitDocument: (change: (current: DrawingDocument) => DrawingDocument) => void
) {
  const document = history.present;
  // Replaced (not cleared) whenever a different drawing is loaded, so it starts with no layers.
  const [cache, setCache] = useState<LayerCache>(() => new WeakMap());

  const [shown, setShown] = useState<Shown | null>(null);

  const layers = useMemo(() => layersForPresent(document, cache, shown), [cache, document, shown]);
  // Remember what was shown, for the next uncached present to carry over from (React's "adjust
  // state while rendering" pattern: re-renders this component immediately, before children).
  if (shown?.document !== document) setShown({ document, layers });

  /** Forgets every entry's layers: the caller is about to load a different drawing (a fresh
   *  history), which starts with none -- its atom ids may reuse the old drawing's. */
  const resetMarkushLayers = useCallback(() => {
    setCache(new WeakMap());
    setShown(null);
  }, []);

  /** An opened Markush file: `document` (about to become the history's only entry) starts with
   *  `openedLayers`. */
  const openMarkushLayers = useCallback((opened: DrawingDocument, openedLayers: MarkushLayers) => {
    setCache(new WeakMap([[opened, openedLayers]]));
    setShown(null);
  }, []);

  /** #674: `next` replaces the structure as an undoable step (a SMILES / template import): it
   *  starts with no layers rather than inheriting the replaced drawing's, while undo still returns
   *  to that drawing's own. */
  const startWithoutLayers = useCallback(
    (next: DrawingDocument) => {
      cache.set(next, EMPTY_MARKUSH_LAYERS);
    },
    [cache]
  );

  /** Commits a Markush edit as one undo step: a fresh entry object (even when only the layers
   *  changed), cached with its layers before history sees it. False when the edit was a no-op. */
  const commitEdit = useCallback(
    (edit: MarkushEdit): boolean => {
      if (edit.document === document && edit.layers === layers) return false;
      const next = edit.document === document ? { ...document } : edit.document;
      cache.set(next, edit.layers);
      commitDocument(() => next);
      return true;
    },
    [cache, commitDocument, document, layers]
  );

  /** The R-group tool's click on an atom. False when it doesn't qualify (not terminal, ...). */
  const labelAtom = useCallback(
    (atomId: string, label: string): boolean => commitEdit(applyRGroupLabel(document, layers, atomId, label)),
    [commitEdit, document, layers]
  );

  /** #654 -- the atom-list tool's click. False when the atom doesn't qualify (locked, ...). */
  const setAtomList = useCallback(
    (atomId: string, allowed: readonly number[]): boolean =>
      commitEdit(applyAtomList(document, layers, atomId, allowed)),
    [commitEdit, document, layers]
  );

  /** #650 -- the R-group tool's click inside a ring (document-normalized point): adds a
   *  ring-interior attachment point. False when the click isn't inside a ring. */
  const attachToRing = useCallback(
    (point: Readonly<{ x: number; y: number }>, label: string): boolean =>
      commitEdit(addRingAttachment(document, layers, point, label)),
    [commitEdit, document, layers]
  );

  /** #652 -- a substituent-table edit (M2's setRGroupDefinition, addRGroupMember, ...) applied to
   *  the derived Markush document, committed as one undo step. False when it changed nothing. */
  const editRGroups = useCallback(
    (change: (markush: MarkushDrawingDocument) => MarkushDrawingDocument): boolean => {
      const markush = toMarkushDrawingDocument(document, layers);
      const next = change(markush);
      if (next === markush) return false;
      return commitEdit({ document, layers: { rGroups: next.rGroups, variablePositions: next.variablePositions } });
    },
    [commitEdit, document, layers]
  );

  /** #656 -- whether these atoms can be bracketed as a repeat unit (connected, concrete, ...). */
  const canBracket = useCallback(
    (atomIds: readonly string[]): boolean => {
      const markush = toMarkushDrawingDocument(document, layers);
      return setRepeatUnit(markush, atomIds, NEW_REPEAT_MIN, NEW_REPEAT_MAX) !== markush;
    },
    [document, layers]
  );

  /** #656 -- brackets `atomIds` (n = 1-3 to start); returns the new unit's id, or null. */
  const bracketAtoms = useCallback(
    (atomIds: readonly string[]): string | null =>
      editRGroups((markush) => setRepeatUnit(markush, atomIds, NEW_REPEAT_MIN, NEW_REPEAT_MAX))
        ? (atomIds[0] ?? null)
        : null,
    [editRGroups]
  );

  /** #656 -- a repeat unit's count range (`countMax` undefined = open-ended); false if refused. */
  const setRepeatCount = useCallback(
    (unitId: string, countMin: number, countMax: number | undefined): boolean =>
      editRGroups((markush) => {
        const unit = markush.variablePositions.find(
          (position) => position.kind === 'repeat-unit' && position.fragmentAtomIds[0] === unitId
        );
        if (unit?.kind !== 'repeat-unit') return markush;
        // Same range again (a field's blur and Apply both firing) is not a new undo step.
        if (unit.countMin === countMin && unit.countMax === countMax) return markush;
        return setRepeatUnit(markush, unit.fragmentAtomIds, countMin, countMax);
      }),
    [editRGroups]
  );

  const removeRepeat = useCallback(
    (unitId: string): boolean => editRGroups((markush) => removeRepeatUnitContaining(markush, unitId)),
    [editRGroups]
  );

  /** #650 (confirmed M2 rule 4) -- commitDocument for the ordinary commands: an edit that deletes
   *  a ring atom also removes the attachment label on that ring, in the same undo step. Identical
   *  to commitDocument for a drawing with no attachment points. */
  const commitWithMarkushCleanup = useCallback(
    (change: (current: DrawingDocument) => DrawingDocument) =>
      commitDocument((current) => {
        const next = change(current);
        return next !== current ? removeOrphanedAttachmentAtoms(layers, current, next) : next;
      }),
    [commitDocument, layers]
  );

  const atomLabels = useMemo(() => markushAtomLabels(document, layers), [document, layers]);
  const issueAtomIds = useMemo(() => markushIssueAtomIds(document, layers), [document, layers]);
  // #674: a drawing is a Markush query while it has a variable position. Definitions alone (left
  // behind when the last R label goes) don't count -- they come back if the label does.
  const hasMarkushContent = layers.variablePositions.length > 0;
  // What formula/validation readouts see: R atoms removed, so an R is never counted as carbon
  // (the same object when there are none).
  const chemistryDocument = useMemo(() => withoutPlaceholderAtoms(document, layers), [document, layers]);
  const markushDocument = useMemo(
    () => (hasMarkushContent ? toMarkushDrawingDocument(document, layers) : null),
    [document, hasMarkushContent, layers]
  );
  // The rail's R-label buttons need these on a plain drawing too; with empty layers (the usual
  // case) skip building a Markush document on every edit.
  const layersEmpty = !hasMarkushContent && Object.keys(layers.rGroups).length === 0;
  const layeredDocument = useMemo(
    () => markushDocument ?? (layersEmpty ? null : toMarkushDrawingDocument(document, layers)),
    [document, layers, layersEmpty, markushDocument]
  );
  const usedLabels = useMemo(() => (layeredDocument ? referencedRGroupLabels(layeredDocument) : []), [layeredDocument]);
  const newLabel = useMemo(() => (layeredDocument ? nextRGroupLabel(layeredDocument) : 'R1'), [layeredDocument]);
  // Every label the table should show: those in use plus any defined but no longer used, R1, R2, ...
  const tableLabels = useMemo(
    () =>
      [...new Set([...usedLabels, ...Object.keys(layers.rGroups)])].sort((a, b) =>
        a.localeCompare(b, undefined, { numeric: true })
      ),
    [layers.rGroups, usedLabels]
  );
  // #656 -- repeat units for the canvas (id = first bracketed atom; units never overlap) and for
  // the count editor.
  const repeatUnits = useMemo<readonly RepeatUnitRef[]>(
    () =>
      markushDocument
        ? markushRepeatUnitMarkers(markushDocument).map((marker) => ({
            id: marker.atomIds[0]!,
            atomIds: marker.atomIds,
            countLabel: marker.countLabel
          }))
        : [],
    [markushDocument]
  );
  const repeatCounts = useMemo(
    () =>
      new Map(
        layers.variablePositions.flatMap((position) =>
          position.kind === 'repeat-unit' && position.fragmentAtomIds[0]
            ? [[position.fragmentAtomIds[0], { countMin: position.countMin, countMax: position.countMax }] as const]
            : []
        )
      ),
    [layers.variablePositions]
  );
  const structureCount = useMemo(
    () => (markushDocument ? markushStructureCount(markushDocument) : null),
    [markushDocument]
  );

  return {
    hasMarkushContent,
    resetMarkushLayers,
    openMarkushLayers,
    startWithoutLayers,
    markushLayers: layers,
    markushDocument,
    markushAtomLabels: atomLabels,
    markushUsedLabels: usedLabels,
    markushNewLabel: newLabel,
    markushTableLabels: tableLabels,
    markushStructureCount: structureCount,
    markushRepeatUnits: repeatUnits,
    markushRepeatCounts: repeatCounts,
    editRGroups,
    canBracket,
    bracketAtoms,
    setRepeatCount,
    removeRepeat,
    markushIssueAtomIds: issueAtomIds,
    chemistryDocument,
    labelAtom,
    setAtomList,
    attachToRing,
    commitWithMarkushCleanup
  };
}
